import React, { useState } from 'react';

// Paginação padrão do painel (25 linhas por página por padrão) — um único lugar para todas as abas.
// Uso:
//   const pager = usePagination(filtered, `${search}|${filtro}`); // resetKey: string dos filtros → volta à pág. 1 ao filtrar
//   ... pager.pageItems.map(...)                                  // (índice global = pager.startIdx + i, p/ ranking/checkbox)
//   <Pagination pager={pager} label="clientes" />
// O resetKey deve ser um primitivo (string/número) ou uma referência estável (ex.: o próprio array
// quando ele só troca em um novo carregamento) — objeto/array recriado a cada render resetaria sempre.

export const PAGE_SIZE_DEFAULT = 25;

export interface Pager<T> {
  pageItems: T[];
  total: number;
  page: number;
  totalPages: number;
  pageSize: number;
  startIdx: number;    // índice (0-based) do 1º item da página dentro da lista completa
  rangeStart: number;  // 1-based, para exibição ("26 a 50 de 132")
  rangeEnd: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: (n: number) => void;
}

export function usePagination<T>(items: T[], resetKey?: unknown): Pager<T> {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeRaw] = useState(PAGE_SIZE_DEFAULT);
  const [prevKey, setPrevKey] = useState(resetKey);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Ajuste de estado durante o render (padrão do React) — evita o efeito pós-paint que causava flash:
  // (1) filtro/busca mudou → volta à 1ª página SÍNCRONO (sem pintar a página antiga na lista nova);
  // (2) senão, se a lista encolheu (exclusão/recarga) abaixo da página atual, reconcilia a página —
  //     sem isso, o estado bruto ficava acima de totalPages e o botão ‹ "engolia" o 1º clique (e a
  //     página podia pular sozinha ao a lista crescer de novo).
  if (!Object.is(prevKey, resetKey)) { setPrevKey(resetKey); setPage(1); }
  else if (page > totalPages) setPage(totalPages);

  const current = Math.min(page, totalPages);
  const startIdx = (current - 1) * pageSize;
  return {
    pageItems: items.slice(startIdx, startIdx + pageSize),
    total,
    page: current,
    totalPages,
    pageSize,
    startIdx,
    rangeStart: total === 0 ? 0 : startIdx + 1,
    rangeEnd: Math.min(startIdx + pageSize, total),
    setPage,
    setPageSize: (n: number) => { setPageSizeRaw(n); setPage(1); },
  };
}

export default function Pagination({ pager, label = 'registros' }: { pager: Pager<any>; label?: string }) {
  if (pager.total === 0) return null; // sem linhas → sem barra (a tela mostra o próprio "vazio")
  // Os handlers partem da página EXIBIDA (clampada) — não do estado bruto — p/ o clique nunca ser "morto".
  return (
    <div className="no-print" style={styles.pagination}>
      <span style={styles.pageInfo}>{pager.rangeStart} a {pager.rangeEnd} de {pager.total} {label}</span>
      <div style={styles.pageControls}>
        <button
          type="button"
          aria-label="Página anterior"
          title="Página anterior"
          style={{ ...styles.pageBtn, ...(pager.page <= 1 ? styles.pageBtnDisabled : {}) }}
          onClick={() => pager.setPage(Math.max(1, pager.page - 1))}
          disabled={pager.page <= 1}
        >
          ‹
        </button>
        <span style={styles.pageCurrent}>{pager.page} / {pager.totalPages}</span>
        <button
          type="button"
          aria-label="Próxima página"
          title="Próxima página"
          style={{ ...styles.pageBtn, ...(pager.page >= pager.totalPages ? styles.pageBtnDisabled : {}) }}
          onClick={() => pager.setPage(Math.min(pager.totalPages, pager.page + 1))}
          disabled={pager.page >= pager.totalPages}
        >
          ›
        </button>
      </div>
      <div style={styles.pageSizeWrap}>
        <label htmlFor="pageSizeSelect" style={styles.pageInfo}>Linhas por página:</label>
        <select
          id="pageSizeSelect"
          aria-label="Linhas por página"
          style={styles.pageSizeSelect}
          value={pager.pageSize}
          onChange={e => pager.setPageSize(Number(e.target.value))}
        >
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 24, padding: '14px 4px', flexWrap: 'wrap' },
  pageInfo: { fontSize: 13, color: '#666' },
  pageControls: { display: 'flex', alignItems: 'center', gap: 8 },
  pageBtn: { border: '1px solid #e0e0e0', background: '#fff', borderRadius: 6, width: 30, height: 30, cursor: 'pointer', fontSize: 16, color: '#333', lineHeight: 1 },
  pageBtnDisabled: { color: '#cbd5e1', cursor: 'not-allowed', background: '#f9fafb' },
  pageCurrent: { fontSize: 13, color: '#333', minWidth: 56, textAlign: 'center' },
  pageSizeWrap: { display: 'flex', alignItems: 'center', gap: 8 },
  pageSizeSelect: { border: '1px solid #e0e0e0', borderRadius: 6, padding: '5px 8px', fontSize: 13, background: '#fff', cursor: 'pointer' },
};
